import { SortingState } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { makeAutoObservable, runInAction } from 'mobx';
import { SchoolYearWithoutHolidayType } from '../../components/SchoolYear/graphql/types';
import { PimAuthClaimsType } from '../../hooks/useAuthClaims';
import { getCacheTag } from '../../hooks/useMemorizedCacheTag';
import {
  MyUserConfigDocument,
  MyUserConfigQuery,
  MyUserConfigQueryVariables,
  ResetMyTableConfigsDocument,
  ResetMyTableConfigsMutation,
  ResetMyTableConfigsMutationVariables,
  ResetMyUiSettingsDocument,
  ResetMyUiSettingsMutation,
  ResetMyUiSettingsMutationVariables,
  SchoolYearsDocument,
  SchoolYearsQuery,
  SchoolYearsQueryVariables,
  SetMySchoolYearDocument,
  SetMySchoolYearMutation,
  SetMySchoolYearMutationVariables,
  UpsertMyTableConfigDocument,
  UpsertMyTableConfigMutation,
  UpsertMyTableConfigMutationVariables,
  UpsertMyUiSettingsDocument,
  UpsertMyUiSettingsMutation,
  UpsertMyUiSettingsMutationVariables,
} from '../../types/planung-graphql-client-defs';
import { urqlClient } from '../../utils/urqlClient';
import {
  defaultSettings,
  parseSettings,
  SETTINGS_REGISTRY,
  SETTINGS_SCOPES,
  SettingsByScope,
  SettingsScope,
} from './settings/schemas';

export interface UserTableConfig {
  table: string;
  hiddenColumns: string[];
  columnsSort: SortingState;
}

type UserConfigSchoolYearType = SchoolYearWithoutHolidayType | null;

/** Wartezeit, bis eine Aenderung an den Ansichtseinstellungen geschrieben wird. */
const SETTINGS_SAVE_DEBOUNCE_MS = 500;

/** Wartezeit, bis eine Aenderung an einer Tabellenkonfiguration geschrieben wird. */
const TABLE_CONFIG_SAVE_DEBOUNCE_MS = 500;

function parseColumnsSort(json: string | null | undefined): SortingState {
  if (!json) return [];
  try {
    const parsed: unknown = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((entry): entry is { id: string; desc?: unknown } => {
        return typeof entry === 'object' && entry !== null && typeof (entry as { id?: unknown }).id === 'string';
      })
      .map((entry) => ({ id: entry.id, desc: entry.desc === true }));
  } catch {
    return [];
  }
}

/**
 * Persoenliche Konfiguration: aktuelles Schuljahr, Tabellenkonfigurationen und
 * Ansichtseinstellungen.
 *
 * Geladen wird genau einmal: `load()` merkt sich sein Promise und ist damit
 * idempotent, auch wenn der Provider mehrfach rendert. Schlaegt das Laden fehl,
 * arbeitet der Store mit Defaults weiter und setzt `error` — die Anwendung darf
 * daran nicht haengen bleiben.
 *
 * Geschrieben wird ueber die Custom-Mutations; die Profil-UUID kommt dabei
 * serverseitig aus den Claims und wird nicht mitgeschickt.
 */
export class UserConfigStore {
  public tableConfigs: Map<string, UserTableConfig> = new Map();
  public selectedSchoolYear: UserConfigSchoolYearType = null;
  public previousSchoolYear: UserConfigSchoolYearType = null;
  public schoolYears: SchoolYearWithoutHolidayType[] = [];
  public language = 'de';
  public loading = false;
  public error: Error | null = null;

  /** Geparste Einstellungen je Bereich; beim Laden mit den Defaults vorbelegt. */
  private settings: Map<SettingsScope, SettingsByScope[SettingsScope]> = new Map();
  /** Welche Bereiche tatsaechlich gespeicherte Werte haben (fuer die Profil-Uebersicht). */
  private storedScopes: Set<SettingsScope> = new Set();

  private loadPromise: Promise<void> | null = null;
  /** Profil, zu dem `loadPromise` gehoert — ein Wechsel muss neu laden. */
  private loadedProfileUuid: string | null = null;
  private settingsSaveTimers: Map<SettingsScope, ReturnType<typeof setTimeout>> = new Map();
  private tableSaveTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private flushListenerAttached = false;

  constructor() {
    makeAutoObservable<UserConfigStore, 'settingsSaveTimers' | 'tableSaveTimers' | 'loadPromise' | 'loadedProfileUuid'>(
      this,
      {
        settingsSaveTimers: false,
        tableSaveTimers: false,
        loadPromise: false,
        loadedProfileUuid: false,
      },
    );

    for (const scope of SETTINGS_SCOPES) {
      this.settings.set(scope, defaultSettings(scope));
    }
  }

  // ─── Laden ────────────────────────────────────────────────────────────────

  /**
   * Laedt die Konfiguration des angemeldeten Profils.
   *
   * Das Promise ist **pro Profil** gecacht: Mehrfachaufrufe fuers selbe Profil
   * liefern dasselbe Promise (ein Re-Render des Providers laedt also nicht erneut
   * und schreibt erst recht nicht), ein anderes Profil laedt neu. Der Store ist ein
   * Modul-Singleton — ohne den Profil-Vergleich behielte er beim Wechsel die
   * Einstellungen, Tabellen und das Schuljahr des vorherigen Profils.
   */
  load(authClaims: PimAuthClaimsType): Promise<void> {
    const profileUuid = authClaims.getProfile().uuid;

    if (this.loadPromise && this.loadedProfileUuid === profileUuid) {
      return this.loadPromise;
    }

    if (this.loadedProfileUuid !== null) {
      this.resetForProfileChange();
    }

    this.loadedProfileUuid = profileUuid;
    this.loadPromise = this.doLoad(authClaims).finally(() => {
      runInAction(() => {
        this.loading = false;
      });
    });

    return this.loadPromise;
  }

  /**
   * Verwirft den Stand des vorherigen Profils.
   *
   * Ausstehende Schreibvorgaenge werden **nicht** geflusht, sondern verworfen: Die
   * Mutationen nehmen die Profil-UUID serverseitig aus den Claims, ein spaeter
   * abgefeuerter Timer wuerde die Werte des alten Profils also dem neuen
   * zuschreiben.
   */
  private resetForProfileChange() {
    for (const timer of this.settingsSaveTimers.values()) clearTimeout(timer);
    for (const timer of this.tableSaveTimers.values()) clearTimeout(timer);
    this.settingsSaveTimers.clear();
    this.tableSaveTimers.clear();

    runInAction(() => {
      this.tableConfigs.clear();
      this.storedScopes.clear();
      for (const scope of SETTINGS_SCOPES) {
        this.settings.set(scope, defaultSettings(scope));
      }
      this.selectedSchoolYear = null;
      this.previousSchoolYear = null;
      this.schoolYears = [];
      this.error = null;
    });
  }

  private async doLoad(authClaims: PimAuthClaimsType): Promise<void> {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    this.attachFlushListener();

    try {
      const [config, schoolYears] = await Promise.all([this.fetchUserConfig(), this.fetchSchoolYears(authClaims)]);

      runInAction(() => {
        this.schoolYears = schoolYears;
        this.tableConfigs = new Map(
          config.tableConfigs.map((tc) => [
            tc.table,
            { table: tc.table, hiddenColumns: [...tc.hiddenColumns], columnsSort: parseColumnsSort(tc.columnsSort) },
          ]),
        );

        for (const scope of SETTINGS_SCOPES) {
          const stored = config.uiSettings.find((entry) => entry.scope === scope);
          this.settings.set(scope, parseSettings(scope, stored?.data));
          if (stored) this.storedScopes.add(scope);
        }
      });

      // Das gespeicherte Schuljahr gewinnt. Nur wenn noch keines gesetzt ist, waehlt
      // der Store eines aus und schreibt es — sonst loest jeder Seitenaufruf eine
      // ueberfluessige Mutation aus.
      const stored = config.schoolYearUuid
        ? (schoolYears.find((year) => year.uuid === config.schoolYearUuid) ?? null)
        : null;

      if (stored) {
        runInAction(() => {
          this.applySchoolYear(stored);
        });
      } else {
        const fallback = pickDefaultSchoolYear(schoolYears);
        if (fallback) {
          await this.setSchoolYear(fallback);
        }
      }
    } catch (error: unknown) {
      runInAction(() => {
        this.error = error instanceof Error ? error : new Error(String(error));
      });
    }
  }

  private async fetchUserConfig(): Promise<MyUserConfigQuery['myUserConfig']> {
    const { data, error } = await urqlClient
      .query<MyUserConfigQuery, MyUserConfigQueryVariables>(MyUserConfigDocument, {}, { requestPolicy: 'network-only' })
      .toPromise();

    // Der globale `mapExchange` in `utils/urqlClient` meldet Fehler und entfernt sie
    // danach aus dem Ergebnis — fehlende Daten sind deshalb das verlaessliche Signal.
    if (error) throw error;
    if (!data?.myUserConfig) throw new Error('myUserConfig lieferte keine Daten');

    return data.myUserConfig;
  }

  private async fetchSchoolYears(authClaims: PimAuthClaimsType): Promise<SchoolYearWithoutHolidayType[]> {
    const { data, error } = await urqlClient
      .query<SchoolYearsQuery, SchoolYearsQueryVariables>(SchoolYearsDocument, {}, getCacheTag('SCHOOL_YEAR'))
      .toPromise();

    if (error) throw error;
    return data?.schoolYears ?? [];
  }

  // ─── Schuljahr ────────────────────────────────────────────────────────────

  /** Setzt das Schuljahr lokal und leitet das vorherige Schuljahr daraus ab. */
  private applySchoolYear(schoolYear: UserConfigSchoolYearType) {
    this.selectedSchoolYear = schoolYear;
    this.previousSchoolYear = findPreviousSchoolYear(this.schoolYears, schoolYear);
  }

  /**
   * Waehlt das Schuljahr und speichert die Auswahl. Der lokale Stand wird zuerst
   * gesetzt, damit die UI sofort reagiert.
   */
  async setSchoolYear(schoolYear: UserConfigSchoolYearType) {
    runInAction(() => {
      this.applySchoolYear(schoolYear);
    });

    const result = await urqlClient
      .mutation<SetMySchoolYearMutation, SetMySchoolYearMutationVariables>(SetMySchoolYearDocument, {
        schoolYearUuid: schoolYear?.uuid ?? null,
      })
      .toPromise();

    this.reportWriteResult('setMySchoolYear', result);
  }

  // ─── Tabellenkonfiguration ────────────────────────────────────────────────

  getTableConfig(table: string): UserTableConfig | undefined {
    return this.tableConfigs.get(table);
  }

  /**
   * Aktualisiert die Tabellenkonfiguration lokal und schreibt sie entprellt.
   * Der lokale Stand wird immer gesetzt — auch fuer eine bisher unbekannte
   * Tabelle —, sonst vergleichen die Hooks weiter gegen einen alten Wert und
   * speichern bei jeder Aenderung erneut.
   */
  upsertTableConfig(table: string, patch: Partial<Omit<UserTableConfig, 'table'>>) {
    const current = this.tableConfigs.get(table);
    const next: UserTableConfig = {
      table,
      hiddenColumns: patch.hiddenColumns ?? current?.hiddenColumns ?? [],
      columnsSort: patch.columnsSort ?? current?.columnsSort ?? [],
    };

    runInAction(() => {
      this.tableConfigs.set(table, next);
    });

    this.scheduleTableConfigSave(table);
  }

  private scheduleTableConfigSave(table: string) {
    clearTimeout(this.tableSaveTimers.get(table));
    this.tableSaveTimers.set(
      table,
      setTimeout(() => void this.flushTableConfig(table), TABLE_CONFIG_SAVE_DEBOUNCE_MS),
    );
  }

  private async flushTableConfig(table: string) {
    const timer = this.tableSaveTimers.get(table);
    if (timer) clearTimeout(timer);
    this.tableSaveTimers.delete(table);

    const config = this.tableConfigs.get(table);
    if (!config) return;

    const result = await urqlClient
      .mutation<UpsertMyTableConfigMutation, UpsertMyTableConfigMutationVariables>(UpsertMyTableConfigDocument, {
        table: config.table,
        hiddenColumns: config.hiddenColumns,
        columnsSort: JSON.stringify(config.columnsSort.map(({ id, desc }) => ({ id, desc: desc === true }))),
      })
      .toPromise();

    this.reportWriteResult(`upsertMyTableConfig(${table})`, result);
  }

  /** Loescht Tabellenkonfigurationen. Ohne Angabe: alle. */
  async resetTableConfigs(tables?: string[]) {
    runInAction(() => {
      if (tables?.length) {
        for (const table of tables) this.tableConfigs.delete(table);
      } else {
        this.tableConfigs.clear();
      }
    });

    const result = await urqlClient
      .mutation<ResetMyTableConfigsMutation, ResetMyTableConfigsMutationVariables>(ResetMyTableConfigsDocument, {
        tables: tables?.length ? tables : null,
      })
      .toPromise();

    this.reportWriteResult('resetMyTableConfigs', result);
  }

  // ─── Schreiben ────────────────────────────────────────────────────────────

  /**
   * Prueft das Ergebnis einer Mutation und meldet einen fehlgeschlagenen Schreibvorgang.
   *
   * Achtung: Der globale `mapExchange` in `utils/urqlClient` meldet Fehler an Sentry,
   * zeigt einen Toast und **entfernt sie danach aus dem Ergebnis** (`error: undefined`,
   * `data: undefined`). Ein reiner `error`-Check liefe hier also ins Leere — das
   * verlaessliche Signal ist, dass `data` fehlt. Geprueft wird trotzdem beides, damit
   * die Erkennung auch ohne diesen Exchange funktioniert.
   *
   * Der lokale Stand bleibt bewusst stehen: Die Nutzerin oder der Nutzer sieht die
   * gewaehlte Einstellung weiter, und `error` macht nachvollziehbar, dass sie den
   * naechsten Seitenaufruf nicht ueberlebt.
   */
  private reportWriteResult(operation: string, result: { data?: unknown; error?: unknown }): boolean {
    if (result.data && !result.error) return true;

    const error =
      result.error instanceof Error ? result.error : new Error(`UserConfig: ${operation} wurde nicht gespeichert`);

    runInAction(() => {
      this.error = error;
    });

    return false;
  }

  // ─── Ansichtseinstellungen ────────────────────────────────────────────────

  getSettings<S extends SettingsScope>(scope: S): SettingsByScope[S] {
    return (this.settings.get(scope) ?? defaultSettings(scope)) as SettingsByScope[S];
  }

  /** Ob fuer den Bereich ueberhaupt etwas gespeichert ist (sonst gilt „Standard"). */
  hasStoredSettings(scope: SettingsScope): boolean {
    return this.storedScopes.has(scope);
  }

  /**
   * Uebernimmt einen Teil-Patch und schreibt ihn entprellt. Aendert der Patch
   * nichts, passiert auch nichts — sonst wuerde jede Hydration der UI-Stores
   * sofort wieder eine Mutation ausloesen.
   */
  updateSettings<S extends SettingsScope>(scope: S, patch: Partial<SettingsByScope[S]>) {
    const current = this.getSettings(scope);
    const next = { ...current, ...patch };

    if (shallowEqual(current as Record<string, unknown>, next as Record<string, unknown>)) return;

    runInAction(() => {
      this.settings.set(scope, next);
      this.storedScopes.add(scope);
    });

    this.scheduleSettingsSave(scope);
  }

  private scheduleSettingsSave(scope: SettingsScope) {
    clearTimeout(this.settingsSaveTimers.get(scope));
    this.settingsSaveTimers.set(
      scope,
      setTimeout(() => void this.flushSettings(scope), SETTINGS_SAVE_DEBOUNCE_MS),
    );
  }

  private async flushSettings(scope: SettingsScope) {
    const timer = this.settingsSaveTimers.get(scope);
    if (timer) clearTimeout(timer);
    this.settingsSaveTimers.delete(scope);

    const result = await urqlClient
      .mutation<UpsertMyUiSettingsMutation, UpsertMyUiSettingsMutationVariables>(UpsertMyUiSettingsDocument, {
        scope,
        version: SETTINGS_REGISTRY[scope].version,
        data: JSON.stringify(this.getSettings(scope)),
      })
      .toPromise();

    this.reportWriteResult(`upsertMyUiSettings(${scope})`, result);
  }

  /** Setzt Bereiche auf die Defaults zurueck. Ohne Angabe: alle Bereiche. */
  async resetSettings(scopes?: SettingsScope[]) {
    const targets = scopes?.length ? scopes : [...SETTINGS_SCOPES];

    runInAction(() => {
      for (const scope of targets) {
        clearTimeout(this.settingsSaveTimers.get(scope));
        this.settingsSaveTimers.delete(scope);
        this.settings.set(scope, defaultSettings(scope));
        this.storedScopes.delete(scope);
      }
    });

    const result = await urqlClient
      .mutation<ResetMyUiSettingsMutation, ResetMyUiSettingsMutationVariables>(ResetMyUiSettingsDocument, {
        scopes: scopes?.length ? scopes : null,
      })
      .toPromise();

    this.reportWriteResult('resetMyUiSettings', result);
  }

  /**
   * Schreibt alle ausstehenden Aenderungen sofort. Wird beim Verlassen der Seite
   * aufgerufen, damit eine Einstellung nicht im Debounce-Fenster verloren geht.
   */
  flushPendingSaves(): Promise<unknown> {
    const pending = [
      ...[...this.settingsSaveTimers.keys()].map((scope) => this.flushSettings(scope)),
      ...[...this.tableSaveTimers.keys()].map((table) => this.flushTableConfig(table)),
    ];

    return Promise.all(pending);
  }

  private attachFlushListener() {
    if (this.flushListenerAttached || typeof window === 'undefined') return;
    this.flushListenerAttached = true;

    // `visibilitychange` statt `beforeunload`: Letzteres feuert auf Mobilgeraeten
    // nicht zuverlaessig, Ersteres auch beim Tab-Wechsel.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') void this.flushPendingSaves();
    });
    window.addEventListener('pagehide', () => void this.flushPendingSaves());
  }
}

/**
 * Das vorherige Schuljahr ist das letzte, das **vor** dem gewaehlten endet —
 * ueber die Daten bestimmt und nicht ueber die Position in der Query, damit eine
 * andere Sortierung das Ergebnis nicht veraendert.
 */
export function findPreviousSchoolYear(
  schoolYears: SchoolYearWithoutHolidayType[],
  selected: UserConfigSchoolYearType,
): UserConfigSchoolYearType {
  if (!selected) return null;

  const candidates = schoolYears
    .filter((year) => year.uuid !== selected.uuid && dayjs(year.end).isBefore(dayjs(selected.start)))
    .sort((a, b) => dayjs(b.end).valueOf() - dayjs(a.end).valueOf());

  return candidates[0] ?? null;
}

/** Laufendes Schuljahr, sonst das naechste kuenftige, sonst das erste bekannte. */
export function pickDefaultSchoolYear(
  schoolYears: SchoolYearWithoutHolidayType[],
): SchoolYearWithoutHolidayType | null {
  const today = dayjs();

  const current = schoolYears.find((year) => dayjs(year.start) <= today && dayjs(year.end) >= today);
  if (current) return current;

  const future = [...schoolYears]
    .filter((year) => dayjs(year.start) > today)
    .sort((a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf())[0];

  return future ?? schoolYears[0] ?? null;
}

function shallowEqual(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);

  for (const key of keys) {
    const left = a[key];
    const right = b[key];
    if (left === right) continue;
    if (Array.isArray(left) && Array.isArray(right)) {
      if (left.length === right.length && left.every((value, index) => value === right[index])) continue;
    }
    return false;
  }

  return true;
}
